import { RefObject, useEffect, useState } from "react";

export type ResponsiveTier = "stack" | "compact" | "full";
export type DenseSurfaceMode = "table" | "grid" | "stack";

export const RESPONSIVE_BREAKPOINTS = {
  stackMax: 767,
  compactMax: 1279,
  shellMobileMax: 1023,
} as const;

export function resolveResponsiveTier(width: number): ResponsiveTier {
  if (width <= RESPONSIVE_BREAKPOINTS.stackMax) return "stack";
  if (width <= RESPONSIVE_BREAKPOINTS.compactMax) return "compact";
  return "full";
}

export function useMediaQuery(query: string, fallback = false): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return fallback;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia(query);
    const applyMatch = () => setMatches(mediaQuery.matches);

    applyMatch();
    mediaQuery.addEventListener("change", applyMatch);
    return () => mediaQuery.removeEventListener("change", applyMatch);
  }, [query]);

  return matches;
}

export function useViewportTier(): ResponsiveTier {
  const [tier, setTier] = useState<ResponsiveTier>(() => {
    if (typeof window === "undefined") return "full";
    return resolveResponsiveTier(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncTier = () => setTier(resolveResponsiveTier(window.innerWidth));

    syncTier();
    window.addEventListener("resize", syncTier);
    return () => window.removeEventListener("resize", syncTier);
  }, []);

  return tier;
}

export function useContainerTier(
  containerRef: RefObject<HTMLElement | null>,
  fallbackTier: ResponsiveTier,
): ResponsiveTier {
  const [tier, setTier] = useState<ResponsiveTier>(fallbackTier);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setTier(fallbackTier);
      return;
    }

    const syncTier = () => {
      const width = container.clientWidth || window.innerWidth;
      setTier(resolveResponsiveTier(width));
    };

    syncTier();

    const observer = new ResizeObserver(syncTier);
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef, fallbackTier]);

  return tier;
}

export function resolveDenseSurfaceMode(width: number): DenseSurfaceMode {
  if (width >= 1080) return "table";
  if (width >= 840) return "grid";
  return "stack";
}

export function useDenseSurfaceMode(
  containerRef: RefObject<HTMLElement | null>,
  fallbackTier: ResponsiveTier,
): DenseSurfaceMode {
  const [mode, setMode] = useState<DenseSurfaceMode>(() => {
    if (typeof window === "undefined") {
      return fallbackTier === "full" ? "table" : fallbackTier === "compact" ? "grid" : "stack";
    }
    return resolveDenseSurfaceMode(window.innerWidth);
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      if (fallbackTier === "full") setMode("table");
      else if (fallbackTier === "compact") setMode("grid");
      else setMode("stack");
      return;
    }

    const syncMode = () => {
      const width = container.clientWidth || window.innerWidth;
      setMode(resolveDenseSurfaceMode(width));
    };

    syncMode();

    const observer = new ResizeObserver(syncMode);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, fallbackTier]);

  return mode;
}
